import React, {
  FC,
  useState,
  KeyboardEvent,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { get } from 'lodash';
import MenuItem from '../MenuItem';
import { InfoTooltip, TooltipPosition } from '../InfoTooltip';
import styles from './GroupByListView.module.css';
import {
  CategorizedList,
  GroupTooltipConfig,
} from '../../../types/SelectInput';
import { translate } from '../../../localization';

type GroupByListViewProps = {
  valueProperty: string;
  list: CategorizedList[];
  onChange: (value: string | string[]) => void;
  multiSelect?: boolean;
  hasOptionSelected: (option: string) => boolean;
  groupTooltips?: GroupTooltipConfig;
  enableSelectAll?: boolean;
  selectedValue?: string | string[];
};

const GroupByListView: FC<GroupByListViewProps> = React.memo(
  ({
    valueProperty,
    list = [],
    onChange,
    multiSelect = false,
    hasOptionSelected,
    groupTooltips = {},
    enableSelectAll = false,
    selectedValue,
  }) => {
    const [groupIndices, setGroupIndices] = useState([0, -1]);
    const [tooltipPositions, setTooltipPositions] = useState<
      Record<string, TooltipPosition>
    >({});

    // Get all items from all groups for Select All functionality
    const allItemsValues = useMemo(() => {
      return list.flatMap(group =>
        group.list.map(item => item[valueProperty] as string)
      );
    }, [list, valueProperty]);

    // Track if Select All is checked
    const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(
      enableSelectAll &&
        multiSelect &&
        Array.isArray(selectedValue) &&
        selectedValue.length === allItemsValues.length &&
        allItemsValues.length > 0
    );

    // Update Select All state when selectedValue changes
    useEffect(() => {
      if (enableSelectAll && multiSelect && Array.isArray(selectedValue)) {
        const allSelected =
          allItemsValues.length > 0 &&
          selectedValue.length === allItemsValues.length &&
          allItemsValues.every(item => selectedValue.includes(item));
        setIsSelectAllChecked(allSelected);
      }
    }, [selectedValue, allItemsValues, enableSelectAll, multiSelect]);
    const itemRefs = useRef<Array<Array<HTMLDivElement | null>>>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const groupTitleRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Function to calculate tooltip position based on scroll and element position
    const calculateTooltipPosition = useCallback(
      (groupTitle: string) => {
        const container = containerRef.current;
        const titleElement = groupTitleRefs.current[groupTitle];

        if (!container || !titleElement) {
          return (
            groupTooltips[groupTitle]?.position || TooltipPosition.TOP_RIGHT
          );
        }

        const containerRect = container.getBoundingClientRect();
        const titleRect = titleElement.getBoundingClientRect();

        // Calculate relative position within the container
        const titleRelativeTop = titleRect.top - containerRect.top;

        // Consider tooltip height and padding
        // If the title is too close to the top of the visible container, use bottom position
        const minSpaceRequired = 80; // Space needed for top tooltip

        if (titleRelativeTop < minSpaceRequired) {
          return TooltipPosition.BOTTOM_RIGHT;
        }

        return groupTooltips[groupTitle]?.position || TooltipPosition.TOP_RIGHT;
      },
      [groupTooltips]
    );

    // Function to update all tooltip positions
    const updateTooltipPositions = useCallback(() => {
      const newPositions: Record<string, TooltipPosition> = {};

      list.forEach(group => {
        if (groupTooltips[group.title]) {
          newPositions[group.title] = calculateTooltipPosition(group.title);
        }
      });

      // Only update state if positions actually changed
      setTooltipPositions(prev => {
        const hasChanged = Object.keys(newPositions).some(
          key => prev[key] !== newPositions[key]
        );
        return hasChanged ? newPositions : prev;
      });
    }, [list, groupTooltips, calculateTooltipPosition]);

    // Scroll handler with immediate update
    const handleScroll = useCallback(() => {
      // Update immediately for better responsiveness
      updateTooltipPositions();
    }, [updateTooltipPositions]);

    const handleSelect = (groupIndex: number, itemIndex: number) => {
      const selectedItem = list[groupIndex].list[itemIndex][valueProperty];

      if (multiSelect) {
        let isToCheckSelectAll = false;
        let newSelectedValues: string[] = [];

        const isValueAlreadySelected =
          Array.isArray(selectedValue) &&
          selectedValue.find(option => option === selectedItem) !== undefined;

        if (isValueAlreadySelected) {
          newSelectedValues = (selectedValue as string[]).filter(
            option => option !== selectedItem
          );
        } else {
          newSelectedValues = [...(selectedValue as string[])];
          newSelectedValues.push(selectedItem as string);
          isToCheckSelectAll =
            newSelectedValues.length === allItemsValues.length;
        }

        setIsSelectAllChecked(isToCheckSelectAll);
        onChange(newSelectedValues);
      } else {
        onChange(selectedItem as string);
      }
    };

    const handleSelectAll = () => {
      if (!multiSelect) return;

      let isToCheckSelectAll = false;
      let newSelectedValues: string[] = [];

      if (!isSelectAllChecked) {
        isToCheckSelectAll = true;
        newSelectedValues = allItemsValues;
      }

      setIsSelectAllChecked(isToCheckSelectAll);
      onChange(newSelectedValues);
    };

    const scrollItemIntoView = (groupIndex: number, itemIndex: number) => {
      itemRefs.current[groupIndex][itemIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      const maxGroupIndex = list.length - 1;
      let [groupIndex, itemIndex] = groupIndices;
      const hasSelectAll = enableSelectAll && multiSelect;

      switch (event.key) {
        case 'ArrowDown':
          // Handle navigation from Select All
          if (hasSelectAll && groupIndex === -1) {
            groupIndex = 0;
            itemIndex = 0;
          } else if (itemIndex < list[groupIndex].list.length - 1) {
            itemIndex += 1;
          } else if (groupIndex < maxGroupIndex) {
            groupIndex += 1;
            itemIndex = 0;
          }
          break;
        case 'ArrowUp':
          if (itemIndex > 0) {
            itemIndex -= 1;
          } else if (groupIndex > 0) {
            groupIndex -= 1;
            itemIndex = list[groupIndex - 1].list.length - 1;
          } else if (hasSelectAll && groupIndex === 0 && itemIndex === 0) {
            // Navigate to Select All
            groupIndex = -1;
            itemIndex = -1;
          }
          break;
        case 'Space':
        case 'Enter':
          if (hasSelectAll && groupIndex === -1) {
            handleSelectAll();
          } else {
            handleSelect(groupIndex, itemIndex);
          }
          break;
        default:
          return;
      }

      setGroupIndices([groupIndex, itemIndex]);

      if (groupIndex >= 0 && itemIndex >= 0) {
        scrollItemIntoView(groupIndex, itemIndex);
      }

      // Update tooltip positions after scrolling
      setTimeout(() => {
        updateTooltipPositions();
      }, 100);

      event.preventDefault(); // Prevent default scrolling behavior
    };

    const setItemRef = (
      groupIndex: number,
      itemIndex: number,
      el: HTMLDivElement | null
    ) => {
      if (!itemRefs.current[groupIndex]) {
        itemRefs.current[groupIndex] = [];
      }
      itemRefs.current[groupIndex][itemIndex] = el;
    };

    useEffect(() => {
      itemRefs.current = list.map(group => Array(group.list.length).fill(null));
      // Initial tooltip position calculation
      updateTooltipPositions();
    }, [list, updateTooltipPositions]);

    // Add scroll event listener and resize observer
    useEffect(() => {
      const container = containerRef.current;
      if (container) {
        // Add scroll listener
        container.addEventListener('scroll', handleScroll);

        // Add resize observer to handle container size changes
        const resizeObserver = new ResizeObserver(() => {
          updateTooltipPositions();
        });
        resizeObserver.observe(container);

        return () => {
          container.removeEventListener('scroll', handleScroll);
          resizeObserver.disconnect();
        };
      }
      return undefined;
    }, [handleScroll, updateTooltipPositions]);

    return (
      <div
        ref={containerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={styles['groupby-list-view-container']}
        role='listbox'
        aria-label='Grouped options list'
      >
        {/* Select All - appears above all groups */}
        {enableSelectAll && multiSelect && allItemsValues.length > 0 && (
          <MenuItem
            key='select-all'
            value='all'
            isSelected={isSelectAllChecked}
            isActive={groupIndices[0] === -1}
            onClick={handleSelectAll}
            selectable={true}
            label={translate('select_all')}
            isDisabled={false}
          />
        )}

        {list.map((group, groupIndex) => (
          <div
            key={group.title}
            role='group'
            className={styles['groupby-list-view-container__list-wrapper']}
            aria-labelledby={group.title}
          >
            {group.title && (
              <div
                ref={el => {
                  groupTitleRefs.current[group.title] = el;
                  // Trigger position update when ref is set
                  if (el) {
                    // Use setTimeout to ensure the element is fully rendered
                    setTimeout(() => {
                      updateTooltipPositions();
                    }, 0);
                  }
                }}
                id={group.title}
                className={
                  styles[
                    'groupby-list-view-container__list-wrapper__title-text'
                  ]
                }
              >
                <div className={styles['group-title-wrapper']}>
                  <span className={styles['group-title-text']}>
                    {group.title} ({group.list.length})
                  </span>
                  {groupTooltips[group.title] && (
                    <InfoTooltip
                      id={`${group.title
                        .toLowerCase()
                        .replace(/\s+/g, '-')}-tooltip`}
                      content={groupTooltips[group.title].text}
                      tooltipPosition={
                        tooltipPositions[group.title] ||
                        groupTooltips[group.title].position ||
                        TooltipPosition.TOP_RIGHT
                      }
                      classes={styles['group-title-tooltip']}
                    />
                  )}
                </div>
              </div>
            )}
            {group.list.map((option, index) => (
              <div
                ref={el => setItemRef(groupIndex, index, el)}
                key={option[valueProperty] as string}
              >
                <MenuItem
                  key={option[valueProperty] as string}
                  value={option[valueProperty] as string}
                  isSelected={hasOptionSelected(
                    option[valueProperty] as string
                  )}
                  isActive={
                    groupIndices[0] === groupIndex && groupIndices[1] === index
                  }
                  onClick={() => handleSelect(groupIndex, index)}
                  selectable={multiSelect}
                  label={get(option, 'label', '')}
                  isDisabled={option.isDisabled as boolean}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
);

GroupByListView.displayName = 'GroupByListView';

export default GroupByListView;
